import { Box, Paper, Skeleton, Typography } from '@mui/material'
import CustomCalendar from 'common/components/CustomCalendar'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'
import { dayjsLocalizer } from 'react-big-calendar'
import useFetchInformeData from '../hooks/useFetchInformeData'
import { InformesContext } from '../providers/InformesProvider'

dayjs.locale('es')

function Event({ event }) {
  return (
    <span style={{ backgroundColor: event.color ? event.color : 'inherit' }}>
      <strong>{event.title}</strong>
    </span>
  )
}

const CalendarView = ({ nombre_informe, activeFilter }) => {
  const {
    active: { structure },
  } = useContext(InformesContext)

  const { response, loading, error } = useFetchInformeData({
    nombre_informe: nombre_informe,
    filtro: activeFilter,
    limit: 9999,
  })

  const [data, setData] = useState([])
  const [eventsList, setEventsList] = useState([])

  const localizer = dayjsLocalizer(dayjs)

  useEffect(() => {
    setData(response?.registros ?? [])
  }, [response])

  const { components } = useMemo(
    () => ({
      components: {
        event: Event,
      },
    }),
    [],
  )

  const calendarLabels = {
    next: 'Día Sig.',
    previous: 'Día Ant.',
    today: 'Hoy',
    month: 'Mes',
    week: 'Semana',
    day: 'Día',
    showMore: (total, remainingEvents, events) => `+${total} más`,
  }

  const eventPropGetter = (event) => {
    return { style: { backgroundColor: event.color ? event.color : 'primary' } }
  }

  useEffect(() => {
    const columnas = structure.response.columnas

    // Buscar alias de columnas relevantes
    const aliasColTitulo = columnas.find((col) => col.titulo_columna === 'TITULO')?.alias_columna
    const aliasColFechaInicio = columnas.find(
      (col) => col.titulo_columna === 'FECHA INICIO',
    )?.alias_columna
    const aliasColFechaFin = columnas.find(
      (col) => col.titulo_columna === 'FECHA FIN',
    )?.alias_columna
    const aliasColColor = columnas.find((col) => col.titulo_columna === 'COLOR')?.alias_columna

    const eventos = data
      .map((item, index) => {
        const fechaInicioRegistro = item[aliasColFechaInicio]
        const fechaFinRegistro = item[aliasColFechaFin]
        const titulo = item[aliasColTitulo]
        const color = item[aliasColColor] ?? 'primary.main'

        const inicio = dayjs(fechaInicioRegistro, 'YYYY-MM-DD HH:mm:ss', true)
        const fin = dayjs(fechaFinRegistro, 'YYYY-MM-DD HH:mm:ss', true)

        // Si las fechas no son válidas, omitir el evento
        if (!inicio.isValid() || !fin.isValid()) {
          return null
        }

        return {
          id: index,
          title: titulo,
          start: inicio.toDate(),
          end: fin.toDate(),
          color,
        }
      })
      .filter(Boolean)

    setEventsList(eventos)
  }, [data, structure])

  return (
    <div key={'calendario-informe'} style={{ margin: '20px' }}>
      {!loading ? (
        <CustomCalendar
          calendarLabels={calendarLabels}
          calendarMinWidth={800}
          components={components}
          endAccessor="end"
          eventPropGetter={eventPropGetter}
          eventsList={eventsList}
          localizer={localizer}
          startAccessor="start"
          step={15}
          timeslots={4}
        />
      ) : (
        <Box sx={{ margin: '20px' }}>
          {/* Encabezado del calendario */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Skeleton variant="rectangular" width={100} height={35} />
            <Typography variant="subtitle1">septiembre 2025</Typography>
            <Skeleton variant="rectangular" width={100} height={35} />
          </Box>

          {/* Celdas del calendario */}
          <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0}>
            {Array.from({ length: 35 }).map((_, index) => (
              <Paper
                key={index}
                variant="outlined"
                square
                sx={{
                  width: 200,
                  height: 150,
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                }}
              >
                <Skeleton variant="text" width={20} />
                {/* Eventos simulados */}
                <Skeleton variant="rectangular" height={20} sx={{ my: 0.5 }} />
                {index % 3 !== 0 && <Skeleton variant="rectangular" height={20} sx={{ my: 0.5 }} />}
              </Paper>
            ))}
          </Box>
        </Box>
      )}
    </div>
  )
}

export default CalendarView
